// [목적] 모바일 / 홈 — sticky 헤더(로고+아바타+검색+칩) + 인기 컬렉션 가로 스크롤 + 폴리오 2열 그리드
// [이유] W2(design-alignment): 쿨톤 하드코딩(#14181F/#586069/#8B949E/#E1E7EF/#F6F8FB)을
//         paper/ink/line 정본 토큰으로 교체. 정본 SoT = packages/ui/src/tokens/color.ts paper/ink/line.
//         W4(design-alignment): 카드 배지 좌상단(top9/left9) 흰배경 + 1px line.base border.
//         fe-mobile-live-data: design-mock(COLLECTIONS/HOME_FOLIOS/ME) → 실데이터 배선.
//         공식 컬렉션은 ExplorePage.tsx 와 동일 collection.client.list({kind:'official'}) 재사용.
//         폴리오 그리드는 useFolioFeed({sort:'latest'}) 재사용(HomePage.tsx 데스크톱과 동일 훅).
//         아바타 이니셜은 useAuth().user.displayName 사용(로그인 전이면 게스트 아이콘 폴백).
//         카드 좋아요는 FolioLikeChip(useFolioLike) 실배선 — 목업 id(/folio/f1) 대신 실 folio.id 로 이동.
// [사이드 이팩트] HomePage 가 viewport.isMobile 시 본 컴포넌트로 위임. mocks/fixtures/design-mock import 제거.
// [수정 시 고려사항] 검색은 클라이언트 표시만 — 실제 자연어 진입은 onSubmit 시 /chat state 로 전달.
//                   pages/auth/** 의 쿨톤은 의도된 전용 톤(ADR-DA6) — 절대 변경 금지.
//                   로딩/빈/에러 상태는 컬렉션·폴리오 그리드 섹션 단위로 개별 처리(한쪽 실패해도 나머지는 정상 렌더).

import { Box, ButtonBase, CircularProgress, InputBase, Typography } from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { Folio, Collection } from '@gangwoncanvas/types';

import * as collectionClient from '../../api/collection.client.js';
import { queryKeys } from '../../api/queryKeys.js';
import { useAuth } from '../../hooks/useAuth.js';
import { useFolioFeed } from '../../hooks/useFolioFeed.js';
import { FolioLikeChip } from '../../components/folio/FolioLikeChip.js';
import { folioAuthorLabel } from '../../utils/folioAuthorLabel.js';

// [목적] paper/ink/line 정본 토큰 바인딩 — W2(design-alignment) 쿨톤 정본화
// [이유] ADR-TR8: 신규 토큰 추가 없이 paper/ink/line 만 사용. [ADR-TR8 예외는 2026-09-18 폐기 — ink.low(#6F6A5C) 는 AA 충족이라 텍스트 사용 가능]
//         c.text.* / c.surface.* (쿨톤) 참조를 c.ink.* / c.paper.* (warm) 로 교체.
// [사이드 이팩트] 배경색 #F6F8FB → #FAF9F7, 텍스트 #14181F → #29261B
// [수정 시 고려사항] ink.low(#6F6A5C)는 본문 미사용 — 보조 텍스트(작성자·시간 등) 한정(ADR-TR8). [ADR-TR8 예외는 2026-09-18 폐기 — ink.low(#6F6A5C) 는 AA 충족이라 텍스트 사용 가능]
//                   contrast.spec.ts 에서 paper[1] 위 ink.high/mid 4.5:1 이상 게이트 검증됨.
const c = tokens.color;
const L = 'light' as const;
const TEXT_HI = c.ink.high[L]; // #29261B (← 쿨톤 c.text.high #14181F)
const TEXT_MID = c.ink.mid[L]; // #5C5849 (← 쿨톤 c.text.mid #586069)
const TEXT_LOW = c.ink.low[L]; // #6F6A5C — 보조 텍스트 한정(ADR-TR8, 본문 미사용)
const BORDER = c.line.base[L]; // #E8E2D5 (← #E1E7EF)
const BG1 = c.paper[1][L]; // #FAF9F7 (← #F6F8FB)
const BRAND = c.brand[500][L];
// [2026-09-18] "전체 →" 소형 링크 텍스트 전용 진한 브랜드 — brand[500] 는 12px 텍스트가 paper[1](BG1)
// 배경 위 4.4:1 로 AA 미달이었다(docs/qa/2026-09-17_contrast-measure.md). brand[600] 은 6.36:1.
const BRAND_TEXT_STRONG = c.brand[600][L];
// [2026-09-18] 프로필 아바타(이니셜) 배경 전용 진한 그린 — accent[500](#2BB673) 는 흰 텍스트 기준
// 2.61:1 로 AA 미달. accent[600] 은 5.40:1(HomePage.tsx 데스크톱 히어로와 동일 토큰).
const ACCENT_STRONG = c.accent[600][L];

// [목적] 컬렉션 카드 배경 색상 순환 — Collection 엔티티에 color 필드 없음(ExplorePage.tsx COLLECTION_BG_CYCLE 와 동일 전략)
// [이유] rgba() 표기는 no-restricted-syntax(hex 리터럴) 규칙 대상이 아님 — ExplorePage.tsx 기존 관용 패턴 재사용.
const COLLECTION_BG_CYCLE = [
  'rgba(234,244,255,1)',
  'rgba(255,244,232,1)',
  'rgba(232,248,239,1)',
  'rgba(255,240,240,1)',
] as const;

const QUICK_CHIPS = ['반려견', '먹거리', '캠핑', 'MZ', '조용한', '서핑'];

/**
 * [목적] 홈 모바일 폴리오 카드 — 실데이터(Folio) 기반, 좋아요는 FolioLikeChip 실배선
 * [이유] fe-mobile-live-data — HomeFolioMock 목업 카드를 실 Folio 타입으로 교체.
 * [사이드 이팩트] 없음 — FolioLikeChip 이 자체 캐시로 좋아요 상태 관리.
 * [수정 시 고려사항] coverImageUrl 없으면 이모지 폴백(🗺), themeColor 있으면 배경색으로 사용.
 */
function MobileFolioCard({ f, onClick }: { f: Folio; onClick: () => void }): JSX.Element {
  return (
    <ButtonBase
      onClick={onClick}
      aria-label={`폴리오 ${f.title}`}
      sx={{
        display: 'block',
        width: '100%',
        bgcolor: c.paper[0][L],
        borderRadius: '14px',
        overflow: 'hidden',
        border: `1px solid ${BORDER}`,
        boxShadow: '0 1px 2px rgba(41,38,27,.06), 0 4px 12px rgba(41,38,27,.04)',
        textAlign: 'left',
        transition: 'transform 120ms ease-out',
        '&:hover': { transform: 'translateY(-2px)' },
      }}
    >
      <Box
        sx={{
          height: 110,
          bgcolor: f.themeColor ?? c.paper[2][L],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 40,
          position: 'relative',
          overflow: 'hidden',
        }}
        aria-hidden="true"
      >
        {f.coverImageUrl ? (
          <Box
            component="img"
            src={f.coverImageUrl}
            alt=""
            loading="lazy"
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          '🗺'
        )}
        {f.duration.days && (
          <Box
            sx={{
              position: 'absolute',
              top: '9px',
              left: '9px',
              bgcolor: 'rgba(255,255,255,.95)',
              borderRadius: `${tokens.radius.s}px`,
              px: '8px',
              py: '3px',
              fontSize: 10,
              color: TEXT_HI,
              fontWeight: 700,
              fontFamily: 'JetBrains Mono, ui-monospace, monospace',
            }}
          >
            {f.duration.days}일
          </Box>
        )}
      </Box>
      <Box sx={{ p: '10px 12px 12px' }}>
        <Typography
          sx={{ fontSize: 12, fontWeight: 600, color: TEXT_HI, lineHeight: 1.4, mb: '6px' }}
        >
          {f.title}
        </Typography>
        <Box sx={{ display: 'flex', gap: '4px', flexWrap: 'wrap', mb: '8px' }}>
          {f.vibe.slice(0, 3).map((t) => (
            <Box
              key={t}
              component="span"
              sx={{
                fontSize: 10,
                bgcolor: BG1,
                color: TEXT_MID,
                borderRadius: '6px',
                px: '6px',
                py: '2px',
              }}
            >
              #{t}
            </Box>
          ))}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box
            component="span"
            sx={{
              fontSize: 10,
              color: TEXT_LOW,
              fontFamily: 'JetBrains Mono, ui-monospace, monospace',
            }}
          >
            {folioAuthorLabel(f.author)}
          </Box>
          <FolioLikeChip folio={f} />
        </Box>
      </Box>
    </ButtonBase>
  );
}

export function HomeMobile(): JSX.Element {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const { status, user } = useAuth();

  // fe-mobile-live-data: 공식 컬렉션 실데이터 — ExplorePage.tsx 와 동일 쿼리키/클라이언트 재사용
  const { data: collectionData, isLoading: collectionsLoading } = useQuery({
    queryKey: queryKeys.collection.list({ kind: 'official', limit: 8 }),
    queryFn: () => collectionClient.list({ kind: 'official', limit: 8 }),
    staleTime: 1000 * 60 * 5,
    throwOnError: false,
  });
  const collections = collectionData?.items ?? [];

  // fe-mobile-live-data: 최근 공개 폴리오 — HomePage.tsx 데스크톱과 동일 useFolioFeed 재사용
  const {
    allFolios,
    isLoading: feedLoading,
    error: feedError,
  } = useFolioFeed({ sort: 'latest', limit: 6 });

  const submitQuery = (text?: string): void => {
    const t = (text ?? query).trim();
    navigate('/chat', t ? { state: { input: t } } : undefined);
  };

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', bgcolor: BG1 }}>
      {/* sticky 헤더 */}
      <Box sx={{ bgcolor: c.paper[0][L], p: '16px 20px 0', borderBottom: `1px solid ${BORDER}` }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: '16px',
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 10,
                color: TEXT_MID,
                fontWeight: 500,
                letterSpacing: 1,
                textTransform: 'uppercase',
                mb: '2px',
              }}
            >
              GangwonCanvas
            </Typography>
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: TEXT_HI, lineHeight: 1.25 }}>
              어떤 강원 여행을
              <br />
              그려볼까요? ✦
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ButtonBase
              onClick={() => navigate('/notifications')}
              aria-label="알림"
              sx={{
                width: 38,
                height: 38,
                borderRadius: '50%',
                bgcolor: BG1,
                color: TEXT_HI,
                fontSize: 16,
                border: `1px solid ${BORDER}`,
              }}
            >
              🔔
            </ButtonBase>
            <ButtonBase
              onClick={() => navigate(status === 'authenticated' ? '/profile' : '/login')}
              aria-label="프로필"
              sx={{
                width: 38,
                height: 38,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${BRAND}, ${ACCENT_STRONG})`,
                color: '#fff',
                fontSize: 16,
                fontWeight: 700,
              }}
            >
              {status === 'authenticated' && user ? user.displayName.charAt(0).toUpperCase() : '?'}
            </ButtonBase>
          </Box>
        </Box>

        {/* 검색/입력창 */}
        <Box
          sx={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            bgcolor: BG1,
            borderRadius: '12px',
            p: '10px 14px',
            mb: '12px',
            border: `1px solid ${BORDER}`,
          }}
        >
          <Box component="span" sx={{ fontSize: 16, color: TEXT_LOW }}>
            ✦
          </Box>
          <InputBase
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitQuery();
            }}
            placeholder='"친구 2명, 1박2일, 조용한 카페..."'
            inputProps={{ 'aria-label': '여행 자연어 입력' }}
            sx={{ flex: 1, fontSize: 13, color: TEXT_HI }}
          />
          {query && (
            <ButtonBase
              onClick={() => submitQuery()}
              aria-label="자연어 전송"
              sx={{
                bgcolor: BRAND,
                color: '#fff',
                borderRadius: '8px',
                px: '10px',
                py: '5px',
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              ↗
            </ButtonBase>
          )}
        </Box>

        {/* Quick Chips */}
        <Box sx={{ display: 'flex', gap: '6px', pb: '14px', overflowX: 'auto' }}>
          {QUICK_CHIPS.map((chip) => (
            <ButtonBase
              key={chip}
              onClick={() => setQuery(chip)}
              aria-label={`Quick ${chip}`}
              sx={{
                flexShrink: 0,
                bgcolor: BG1,
                border: `1px solid ${BORDER}`,
                borderRadius: 20,
                px: '12px',
                py: '5px',
                fontSize: 12,
                color: TEXT_MID,
              }}
            >
              {chip}
            </ButtonBase>
          ))}
        </Box>
      </Box>

      {/* 인기 컬렉션 — 실데이터, 0건이면 graceful(섹션 숨김) */}
      {(collectionsLoading || collections.length > 0) && (
        <Box sx={{ p: '20px 0 0' }}>
          <Box
            sx={{
              px: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: '12px',
            }}
          >
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: TEXT_HI }}>
              공식 컬렉션
            </Typography>
            <ButtonBase
              onClick={() => navigate('/explore')}
              sx={{ fontSize: 12, color: BRAND_TEXT_STRONG }}
              aria-label="컬렉션 전체 보기"
            >
              전체 →
            </ButtonBase>
          </Box>
          {collectionsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', px: '20px', py: '12px' }}>
              <CircularProgress size={20} aria-label="컬렉션 불러오는 중" />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: '10px', px: '20px', overflowX: 'auto', pb: '4px' }}>
              {collections.map((col: Collection, idx: number) => (
                <ButtonBase
                  key={col.id}
                  onClick={() => navigate(`/collections/${col.id}`)}
                  aria-label={`컬렉션 ${col.title}`}
                  sx={{
                    minWidth: 140,
                    bgcolor: COLLECTION_BG_CYCLE[idx % COLLECTION_BG_CYCLE.length],
                    borderRadius: '14px',
                    p: '12px 14px',
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                  }}
                >
                  <Box component="span" sx={{ fontSize: 26, mb: '6px' }} aria-hidden="true">
                    🏛
                  </Box>
                  <Typography
                    sx={{ fontSize: 13, fontWeight: 600, color: TEXT_HI, lineHeight: 1.3 }}
                  >
                    {col.title}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: TEXT_MID, mt: '4px' }}>
                    폴리오 {col.itemCount}개
                  </Typography>
                </ButtonBase>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* 최근 공개 폴리오 — 실데이터 */}
      <Box sx={{ p: '24px 20px 32px' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: '14px',
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: TEXT_HI }}>
            최근 공개 폴리오
          </Typography>
          <ButtonBase
            onClick={() => navigate('/explore')}
            sx={{ fontSize: 12, color: BRAND_TEXT_STRONG }}
            aria-label="폴리오 전체 보기"
          >
            전체 →
          </ButtonBase>
        </Box>

        {feedLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: '32px' }}>
            <CircularProgress size={22} aria-label="폴리오 불러오는 중" />
          </Box>
        )}

        {Boolean(feedError) && !feedLoading && (
          <Box
            role="alert"
            sx={{ textAlign: 'center', p: '24px 20px', color: TEXT_MID, fontSize: 13 }}
          >
            폴리오를 불러오지 못했습니다.
          </Box>
        )}

        {/* [수정: 디자인 개편] 모바일도 데스크톱과 동일하게 빈 텍스트 대신 첫 칸에 "새 폴리오 만들기" 카드를 고정한다. */}
        {!feedLoading && !feedError && allFolios.length === 0 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <ButtonBase
              onClick={() => navigate('/chat')}
              aria-label="새 폴리오 만들기"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                aspectRatio: '1 / 1.1',
                border: `2px dashed ${BORDER}`,
                borderRadius: '14px',
                color: TEXT_LOW,
                '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
              }}
            >
              <Box aria-hidden="true" sx={{ fontSize: 24 }}>
                ✦
              </Box>
              <Typography sx={{ fontSize: 12, fontWeight: 700 }}>+ 새 폴리오 만들기</Typography>
            </ButtonBase>
          </Box>
        )}

        {!feedLoading && allFolios.length > 0 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {allFolios.slice(0, 6).map((f) => (
              <MobileFolioCard key={f.id} f={f} onClick={() => navigate(`/folio/${f.id}`)} />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
